import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { CalendarOff, Wallet } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { KorektaCeny } from '@/components/KorektaCeny';
import { Button } from '@/components/ui/button';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { listGroups as listEmpGroups } from '@/lib/employee-groups-api';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/lib/auth';
import { useIslands } from '@/lib/islands';
import { listMachines } from '@/lib/machines-api';
import { czasSps, dataSps, granicaDnia, granicaZPola } from '@/lib/czas-sps';
import {
  KODY_Z_NAZWA,
  fetchCentraKosztowe,
  listTransactions,
  type TransactionRow,
  type TransCode,
} from '@/lib/transactions-api';
import { LIMIT_EKSPORTU, zbierzWszystkieStrony, type FormatEksportu } from '@/lib/export';
import { cn } from '@/lib/utils';
import { nazwaKodu } from '@/lib/kody-operacji';
import { iloscNaEkran, iloscZeZnakiem } from '@/lib/znak-ilosci';


type Preset = 'all' | 'today' | 'd7' | 'd30' | 'mtd' | 'custom';

const nfMoney = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });
const nfInt = new Intl.NumberFormat('pl-PL');

/**
 * ⚠️ CZAS WYDANIA NIE PRZECHODZI PRZEZ `Date` — patrz `lib/czas-sps.ts`. To odczyt zegara
 * maszyny, nie chwila, więc `toLocaleString` przesuwałby go o strefę przeglądarki.
 */
const fmtDateTime = czasSps;
const fmtDate = dataSps;

/**
 * Znak niesie TRANSCODE (WN = wydanie), więc kwoty pokazujemy dodatnie.
 *
 * ⚠️ `DD` dostaje kolor NEUTRALNY świadomie: kod występuje we wszystkich prawdziwych bazach, ale
 * jego znaczenia jeszcze nie potwierdziliśmy. Nadanie mu barwy „wydanie" albo „uzupełnienie"
 * byłoby zgadywaniem semantyki i pokazywaniem tego zgadywania jako faktu.
 */
const CODE_VARIANT: Record<string, 'destructive' | 'success' | 'warning' | 'neutral'> = {
  WN: 'destructive',
  REN: 'success',
  IA: 'warning',
  DD: 'neutral',
};

/**
 * Kolor kodu — NEUTRALNY dla wszystkiego, czego nie znamy.
 *
 * ⚠️ MAPA MUSI MIEĆ WARTOŚĆ DOMYŚLNĄ, odkąd kody nie są zamkniętą listą. `Record<TransCode, …>`
 * przy `TransCode = string` nie zmusza już do wypisania wszystkich, więc brak `?? 'neutral'`
 * skończyłby się `undefined` w propsie i kodem bez tła.
 */
const wariantKodu = (code: string) => CODE_VARIANT[code] ?? 'neutral';

/**
 * Zakres jako CZAS ŚCIENNY, nie ISO z przesunięciem.
 *
 * ⚠️ WCZEŚNIEJ BYŁO TU `.toISOString()` I TO BYŁ BŁĄD O DWIE GODZINY. Dla polskiej przeglądarki
 * `new Date(2026,7,5).toISOString()` daje `2026-08-04T22:00:00.000Z`, więc filtr „od 5 sierpnia"
 * łapał też wieczór 4 sierpnia — a wydanie o 22:30 trafiało w raporcie do sąsiedniego dnia.
 * Kolumna `ts` jest czasem ściennym maszyny, więc granica też musi nim być. Serwer napisu ze
 * strefą już nie przyjmie, żeby ten błąd nie mógł wrócić po cichu.
 *
 * `Date` służy tu WYŁĄCZNIE do arytmetyki kalendarzowej (odjęcie dni, pierwszy dzień miesiąca),
 * a nie do przenoszenia chwili — wynik zawsze wychodzi przez `granicaDnia`.
 */
function rangeFor(preset: Preset, custom: { from: string; to: string }): { from?: string; to?: string } {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  switch (preset) {
    case 'today':
      return { from: granicaDnia(now) };
    case 'd7':
      return { from: granicaDnia(new Date(startOfDay(now).getTime() - 6 * 86_400_000)) };
    case 'd30':
      return { from: granicaDnia(new Date(startOfDay(now).getTime() - 29 * 86_400_000)) };
    case 'mtd':
      return { from: granicaDnia(new Date(now.getFullYear(), now.getMonth(), 1)) };
    case 'custom':
      return {
        from: custom.from ? granicaZPola(custom.from) : undefined,
        // „do" włącznie — domykamy do końca dnia, inaczej ostatni dzień wypadałby z wyniku
        to: custom.to ? granicaZPola(custom.to, true) : undefined,
      };
    default:
      return {};
  }
}

export function Transactions() {
  const { t } = useI18n();
  const { groupId, island } = useIslands();
  const { can } = useAuth();

  const [state, setState] = useState<DataTableState>({
    search: '',
    page: 1,
    pageSize: 50,
    sort: 'ts',
    dir: 'desc',
  });
  const [machineId, setMachineId] = useState<string>('');
  const [transCode, setTransCode] = useState<string>('');
  const [employeeGroupId, setEmployeeGroupId] = useState<string>('');
  /**
   * ⚠️ `undefined` znaczy BEZ FILTRA, a pusty napis znaczy BEZ CENTRUM — to dwie różne rzeczy.
   * Na DBW 6258 wydań nie ma centrum wcale i bez tego rozróżnienia byłyby nieosiągalne.
   */
  const [costCenter, setCostCenter] = useState<string | undefined>(undefined);
  const [preset, setPreset] = useState<Preset>('d30');
  const [custom, setCustom] = useState({ from: '', to: '' });

  /**
   * FILTRY WYMAGAJĄ INNYCH MODUŁÓW niż sam raport.
   *
   * Lista automatów jest za `machines`, lista grup pracowniczych za `groups` — a konto KLIENTA
   * ma w sufycie (§8a) wyłącznie `transactions`. Bez `enabled` te dwa zapytania kończyłyby się
   * `403` i ekran raportu pokazywałby błąd, choć sam raport działa.
   *
   * Filtr, którego nie da się wypełnić, po prostu się nie pokazuje — zamiast pustej listy
   * rozwijanej, która wygląda na „nie ma automatów".
   */
  const canFilterByMachine = can('machines');
  const canFilterByEmpGroup = can('groups');

  const machines = useQuery({
    queryKey: ['machines', 'picker', groupId],
    queryFn: () =>
      listMachines({ groupId: groupId!, page: 1, pageSize: 200, sort: 'vmid', dir: 'asc', onlyOnline: false }),
    enabled: Boolean(groupId) && canFilterByMachine,
  });

  const empGroups = useQuery({
    queryKey: ['employee-groups', 'picker', groupId],
    queryFn: () => listEmpGroups({ groupId: groupId!, page: 1, pageSize: 200, sort: 'name', dir: 'asc' }),
    enabled: Boolean(groupId) && canFilterByEmpGroup,
  });

  /** Lista wartosci do wyboru - czytana z WYDAN, nie z katalogu centrow (patrz serwis API). */
  const centra = useQuery({
    queryKey: ['transactions', 'centra-kosztowe', groupId],
    queryFn: () => fetchCentraKosztowe(groupId!),
    enabled: Boolean(groupId),
  });

  const range = rangeFor(preset, custom);

  const params = {
    ...state,
    groupId: groupId ?? '',
    machineId: machineId || undefined,
    transCode: (transCode || undefined) as TransCode | undefined,
    employeeGroupId: employeeGroupId || undefined,
    costCenter,
    from: range.from,
    to: range.to,
  };

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: ['transactions', params],
    queryFn: () => listTransactions(params),
    enabled: Boolean(groupId),
  });

  /**
   * KODY W LIŚCIE FILTRA — znane z nazwy PLUS każdy, który realnie się pojawił.
   *
   * ⚠️ ZBIÓR TYLKO ROŚNIE I TAK MUSI BYĆ. Podsumowanie liczy się na zbiorze PRZEFILTROWANYM, więc
   * po wybraniu `WN` zostaje w nim wyłącznie `WN`. Gdyby lista brała się wprost z bieżącej
   * odpowiedzi, pierwszy wybór skasowałby wszystkie pozostałe pozycje i nie dałoby się wrócić
   * — filtr zjadałby sam siebie.
   */
  /**
   * Czy otwarte jest okno korekty cen.
   *
   * ⚠️ WCHODZI PRZYCISKIEM NAD LISTĄ, nie ikonką przy wierszu — zgłoszone wprost: przy wierszu
   * „trzeba jakoś szukać na liście transakcji". Towar i zakres dat wybiera się w oknie, więc lista
   * nie musi być w ogóle ustawiona; z ekranu bierzemy tylko PODPOWIEDŹ zakresu i automat.
   */
  const [korekta, setKorekta] = useState(false);

  /**
   * NAZWY CZYNNYCH FILTRÓW i ich czyszczenie — pasek nad tabelą (zgłoszone 02.09.2026).
   *
   * ⚠️ OKRES JEST FILTREM, ALE `d30` NIE: to wartość początkowa ekranu. Pokazujemy go dopiero,
   * gdy ktoś wybrał inny — inaczej pasek świeciłby na każdym wejściu w transakcje.
   *
   * ⚠️ CZYSZCZENIE WRACA DO `d30` I PUSTEGO ZAKRESU WŁASNEGO. Zostawienie własnych dat przy
   * presecie `d30` dawałoby stan, którego nie da się wyklikać ręcznie.
   */
  const aktywneFiltry = [
    machineId
      ? t('tx.machine') +
        ': ' +
        (() => {
          const m = (machines.data?.rows ?? []).find((x) => x.id === machineId);
          return m === undefined ? machineId : `VMID ${m.vmid} — ${m.name}`;
        })()
      : null,
    transCode ? t('tx.code') + ': ' + transCode : null,
    employeeGroupId
      ? t('tx.groupAtIssue') +
        ': ' +
        ((empGroups.data?.rows ?? []).find((g) => g.id === employeeGroupId)?.name ?? employeeGroupId)
      : null,
    costCenter === undefined
      ? null
      : t('tx.costCenter') + ': ' + (costCenter === '' ? t('tx.noCostCenter') : costCenter),
    preset === 'd30'
      ? null
      : t('tx.period') +
        ': ' +
        (preset === 'custom' ? `${custom.from || '—'} … ${custom.to || '—'}` : t(`tx.preset.${preset}`)),
  ].filter((x): x is string => x !== null);

  const wyczyscFiltry = () => {
    setMachineId('');
    setTransCode('');
    setEmployeeGroupId('');
    setCostCenter(undefined);
    setPreset('d30');
    setCustom({ from: '', to: '' });
    resetPage();
  };

  const [kodyWidziane, setKodyWidziane] = useState<string[]>([...KODY_Z_NAZWA]);
  useEffect(() => {
    const nowe = Object.keys(data?.summary?.byCode ?? {});
    if (nowe.length === 0) return;
    setKodyWidziane((prev) => {
      const brakujace = nowe.filter((c) => !prev.includes(c));
      return brakujace.length === 0 ? prev : [...prev, ...brakujace].sort();
    });
  }, [data]);
  const kodyDoFiltra = kodyWidziane;

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  // Filtry wskazujące rekordy TEJ wyspy (automat, grupa) muszą zniknąć przy przełączeniu wyspy.
  // Inaczej po zmianie wyspy zostaje id z poprzedniej: lista wraca cicho pusta, a pasek aktywnych
  // filtrów pokazuje wtedy „—", bo nazwy nie da się znaleźć w nowym zbiorze — czyli filtr działa,
  // ale nie widać, że działa.
  useEffect(() => {
    setMachineId('');
    setEmployeeGroupId('');
  }, [groupId]);
  const resetPage = useCallback(() => onStateChange({ page: 1 }), [onStateChange]);

  const columns = useMemo<ColumnDef<TransactionRow>[]>(
    () => [
      {
        id: 'ts',
        header: t('tx.when'),
        meta: { exportValue: (r) => fmtDateTime(r.ts) },
        cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{fmtDateTime(row.original.ts)}</span>,
      },
      {
        id: 'transCode',
        header: t('tx.code'),
        meta: { exportValue: (r) => `${r.transCode} (${nazwaKodu(t, r.transCode)})`, align: 'center' },
        cell: ({ row }) => (
          <Badge variant={wariantKodu(row.original.transCode)} title={nazwaKodu(t, row.original.transCode)}>
            {row.original.transCode}
          </Badge>
        ),
      },
      {
        id: 'machine',
        header: t('tx.machine'),
        enableSorting: false,
        meta: { exportValue: (r) => `VMID ${r.machine.vmid} — ${r.machine.name}` },
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            <span className="font-mono text-xs text-muted-foreground">{row.original.machine.vmid}</span>{' '}
            {row.original.machine.name}
          </span>
        ),
      },
      {
        /**
         * TOWAR: NAZWA I NUMER, JEDNO POD DRUGIM.
         *
         * ══════════════════════════════════════════════════════════════════════════════════════
         *  ⚠️⚠️ ZAMÓWIONE 11.09.2026: „w kolumnie towar dodaj małym tekstem też numer towaru,
         *  bo teraz mamy tylko nazwę, a te RWKB to «WKŁADY BAWEŁNIANE»". Numer jest kluczem
         *  naturalnym towaru w SPS i to po nim człowiek szuka wydania, gdy przychodzi
         *  z alertem albo z planogramu — nazwa bywa myląca (trzy rozmiary jednej rękawicy mają
         *  tę samą nazwę i różne numery).
         *
         *  ⚠️ SZUKAJKA OBEJMUJE TERAZ OBA POLA (`transactions.service.ts`, obie ścieżki
         *  zapytania — lista i podsumowanie). Numer pokazany, ale niewyszukiwalny, byłby
         *  gorszy od braku: widać go, a wpisanie go do szukajki daje pustą listę.
         * ══════════════════════════════════════════════════════════════════════════════════════
         */
        id: 'item',
        header: t('tx.item'),
        enableSorting: false,
        meta: { exportValue: (r) => (r.item === null ? '' : `${r.item.name} (${r.item.itemNumber})`) },
        cell: ({ row }) =>
          row.original.item === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="block">
              {row.original.item.name}
              <span className="block font-mono text-[11px] text-muted-foreground">
                {row.original.item.itemNumber}
              </span>
            </span>
          ),
      },
      {
        /**
         * NUMER U DOSTAWCY — zamówione 02.09.2026: „dodaj w transakcjach «Numer dostawcy» między
         * Towar a Pracownik (czyli numer P0)".
         *
         * ══════════════════════════════════════════════════════════════════════════════════════
         *  ⚠️ TA SAMA ETYKIETA CO NA ARTYKUŁACH (`itm.supplierPart`), a nie własna w `tx.*`.
         *  To jedno i to samo pole SPS (`Items.SUPPLIERPARTNUMBER`) i musi się nazywać wszędzie
         *  tak samo — inaczej ktoś porównuje kolumny z dwóch ekranów, nie wiedząc, że patrzy
         *  na to samo. (Kreator raportów ma dłuższą nazwę, bo tam trzeba ją odróżnić od numeru
         *  towaru na liście kilkudziesięciu kolumn do wyboru.)
         *
         *  ⚠️ `nullable().optional()` W SCHEMACIE, więc tu pusto = „nie wiemy albo nie ma".
         *  Numer u dostawcy nie jest wymagany w SPS; puste pokazujemy myślnikiem, a nie zerem.
         *
         *  ⚠️ BEZ SORTOWANIA: serwer sortuje wydania po kolumnach z indeksem (czas, TLID, VMID).
         *  Sortowanie po numerze dostawcy wymagałoby zapytania po `Items`, a lista wydań ma
         *  u klientów setki tysięcy wierszy.
         * ══════════════════════════════════════════════════════════════════════════════════════
         */
        id: 'supplierPart',
        header: t('itm.supplierPart'),
        enableSorting: false,
        meta: { exportValue: (r) => r.item?.supplierPartNumber ?? '' },
        cell: ({ row }) =>
          row.original.item?.supplierPartNumber ?? <span className="text-muted-foreground">—</span>,
      },
      {
        /**
         * NUMER KARTY — zamówione 27.10.2026: „potrzebuję w domyślnym widoku widzieć users.usernumber
         * czyli numer karty pracownika. najlepiej między numerem u dostawcy a «pracownikiem»".
         *
         * ══════════════════════════════════════════════════════════════════════════════════════
         *  ⚠️ NUMER Z CHWILI WYDANIA (`TransactionLog.USERNUMBER`), NIE BIEŻĄCY Z KARTOTEKI. Dziś to
         *  ta sama wartość co `Users.USERNUMBER`; różnią się dopiero po zmianie numeru karty — wtedy
         *  wydanie zostaje pod starym numerem, a pod nim było wydane. Ten sam wybór co w Raportowaniu
         *  (`rap.col.card`), żeby dwa ekrany nie pokazywały przy jednym wydaniu dwóch numerów.
         *
         *  ⚠️ Etykieta z kartoteki pracowników (`emp.cardNumber`) — jedno pole, jedna nazwa.
         *  ⚠️ Szukajka obejmuje też ten numer (`transactions.service.ts`), patrz kolumna „Towar".
         * ══════════════════════════════════════════════════════════════════════════════════════
         */
        id: 'userNumberAtIssue',
        header: t('emp.cardNumber'),
        enableSorting: false,
        meta: { exportValue: (r) => r.userNumberAtIssue ?? '' },
        cell: ({ row }) =>
          (row.original.userNumberAtIssue ?? '') === '' ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="font-mono text-xs">{row.original.userNumberAtIssue}</span>
          ),
      },
      {
        id: 'employee',
        header: t('tx.employee'),
        enableSorting: false,
        meta: { exportValue: (r) => r.employee?.name ?? '' },
        cell: ({ row }) =>
          row.original.employee?.name ?? <span className="text-muted-foreground">—</span>,
      },
      {
        /**
         * Grupa Z CHWILI WYDANIA, nie bieżąca.
         *
         * Dzięki temu raport za zamknięty okres nie zmienia się po przeniesieniu człowieka.
         * Pusta lista to „nie wiemy" (starsza synchronizacja nie przenosiła tych kolumn),
         * więc pokazujemy „—", a NIE „bez grupy" — to byłoby zgadywanie za dane.
         */
        id: 'groupsAtIssue',
        header: t('tx.groupAtIssue'),
        enableSorting: false,
        meta: { exportValue: (r) => r.groupsAtIssue.join(', ') },
        cell: ({ row }) =>
          row.original.groupsAtIssue.length === 0 ? (
            <span className="text-muted-foreground" title={t('tx.groupAtIssueUnknown')}>
              —
            </span>
          ) : (
            <span className="font-mono text-xs">{row.original.groupsAtIssue.join(', ')}</span>
          ),
      },
      {
        /**
         * CENTRUM KOSZTOWE Z CHWILI WYDANIA - napis dokladnie taki, jak zapisal go automat.
         *
         * Pusta wartosc to "bez centrum" i tak ja podpisujemy, a nie kreska: kreska znaczy u nas
         * "nie wiemy" (jak przy grupach), a tu wiemy - centrum po prostu nie bylo.
         */
        id: 'costCenterAtIssue',
        header: t('tx.costCenter'),
        enableSorting: false,
        meta: { exportValue: (r) => r.costCenterAtIssue ?? '' },
        cell: ({ row }) =>
          (row.original.costCenterAtIssue ?? '') === '' ? (
            <span className="text-muted-foreground text-xs">{t('tx.noCostCenter')}</span>
          ) : (
            <span className="font-mono text-xs">{row.original.costCenterAtIssue}</span>
          ),
      },
      /**
       * ILOŚĆ ZE ZNAKIEM WPŁYWU NA STAN — patrz `lib/znak-ilosci.ts`.
       *
       * ⚠️ TEN SAM ZNAK IDZIE DO EKSPORTU. Gdyby ekran pokazywał „−3", a plik „3", to rozjechanie
       * ujawniłoby się dopiero w arkuszu u kogoś innego — czyli w najgorszym możliwym miejscu.
       */
      {
        id: 'qty',
        header: t('tx.qty'),
        meta: { exportValue: (r) => iloscZeZnakiem(r.transCode, r.qty), align: 'right' },
        cell: ({ row }) => (
          <span className="tabular-nums">{iloscNaEkran(row.original.transCode, row.original.qty)}</span>
        ),
      },
      {
        id: 'unitCost',
        header: t('tx.unitCost'),
        enableSorting: false,
        meta: { exportValue: (r) => r.unitCost.toFixed(2), align: 'right' },
        cell: ({ row }) => <span className="tabular-nums">{nfMoney.format(row.original.unitCost)}</span>,
      },
      {
        id: 'value',
        header: t('tx.value'),
        enableSorting: false,
        meta: { exportValue: (r) => r.value.toFixed(2), align: 'right' },
        cell: ({ row }) => <span className="font-medium tabular-nums">{nfMoney.format(row.original.value)}</span>,
      },
      {
        id: 'tlid',
        header: 'TLID',
        meta: { exportValue: (r) => r.localTlid, align: 'right' },
        cell: ({ row }) => (
          <span className="font-mono text-[11px] text-muted-foreground" title={`localTLID ${row.original.localTlid}`}>
            {row.original.tlid}
          </span>
        ),
      },
    ],
    [t],
  );

  /**
   * Eksport całego przefiltrowanego zbioru, z JAWNĄ ODMOWĄ, gdy nie mieści się w limicie formatu.
   *
   * ⚠️ DOTĄD TA LISTA UCINAŁA ZBIÓR I ODDAWAŁA PLIK. Po przekroczeniu 20 000 wierszy pętla
   * przerywała, leciał dymek „ucięte", a plik i tak się pobierał — czyli na dysku lądował raport
   * wyglądający na kompletny, którego jedynym ostrzeżeniem było powiadomienie znikające po
   * kilku sekundach. Teraz odmowa jest błędem eksportu: albo plik jest cały, albo go nie ma.
   */
  const fetchAllRows = useCallback(
    (format: FormatEksportu, postep?: (ile: number, zIlu: number) => void): Promise<TransactionRow[]> => {
      if (!groupId) return Promise.resolve([]);
      return zbierzWszystkieStrony<TransactionRow>({
        limit: LIMIT_EKSPORTU[format],
        postep,
        pobierz: (page, pageSize) => listTransactions({ ...params, page, pageSize }),
        zaDuzo: (total, limit) =>
          new Error(
            t('tx.exportTooBig')
              .replace('{limit}', nfInt.format(limit))
              .replace('{total}', nfInt.format(total))
              .replace('{format}', format.toUpperCase()),
          ),
      });
    },
    [params, groupId, t],
  );

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  const s = data?.summary;

  /**
   * Opis aktywnych filtrów.
   * Panel filtrów jest domyślnie zwinięty, więc bez tego użytkownik widziałby np. 1295 z 2700
   * pozycji nie wiedząc, że działa okno 30 dni — ukryty filtr to zafałszowany obraz danych.
   */
  const activeFilters = [
    preset === 'custom'
      ? [custom.from, custom.to].filter(Boolean).join(' – ') || t('tx.preset.all')
      : t(`tx.preset.${preset}`),
    machineId ? (machines.data?.rows.find((m) => m.id === machineId)?.name ?? '') : null,
    transCode ? `${transCode} — ${t(`tx.code.${transCode as TransCode}`)}` : null,
    // plakietka mówi WPROST, że to grupa z chwili wydania — inaczej czytałoby się ją
    // jako filtr po bieżącym członkostwie i liczby wyglądałyby na niepełne
    employeeGroupId
      ? `${t('tx.groupAtIssue')}: ${empGroups.data?.rows.find((g) => g.id === employeeGroupId)?.name ?? ''}`
      : null,
    state.search ? `„${state.search}"` : null,
  ].filter(Boolean);

  /**
   * PUSTO Z POWODU OKNA CZASU — mówimy to wprost.
   *
   * Zero pozycji przy 20 599 wierszach w bazie wyglądało jak zepsuty portal. Rozróżniamy więc
   * „nic tu nie ma" od „nic nie ma W TYM OKRESIE, a najnowsze jest wtedy" — i podajemy datę,
   * żeby nie trzeba było zgadywać, którym zakresem szukać.
   */
  const pusteZPowoduOkna =
    !isPending && (data?.total ?? 0) === 0 && preset !== 'all' && data?.newestOutsideRange != null;

  return (
    <div className="space-y-4">
      {pusteZPowoduOkna && (
        <div className="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 p-4 text-sm">
          <CalendarOff className="mt-0.5 size-4 shrink-0" />
          <span>
            {t('tx.emptyForRange').replace('{range}', t(`tx.preset.${preset}`).toLowerCase())}{' '}
            {t('tx.newestOutsideRange').replace('{date}', fmtDate(data!.newestOutsideRange!))}
          </span>
        </div>
      )}

      {s && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-border bg-card px-4 py-3 text-sm">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-muted-foreground">{t('tx.summaryFor')}</span>
            {activeFilters.map((f) => (
              <Badge key={f} variant="neutral">
                {f}
              </Badge>
            ))}
          </span>
          <span>
            <span className="text-muted-foreground">{t('tx.issued')}: </span>
            <span className="font-medium tabular-nums">{nfInt.format(s.issuedQty)}</span>
            <span className="ml-1 text-muted-foreground">({nfMoney.format(s.issuedValue)})</span>
          </span>
          {/* ⚠️ Z DANYCH, NIE Z LISTY W KODZIE. Wcześniej stało tu `['WN','REN','IA']`, więc
              podsumowanie milczało o każdym innym kodzie — także o `DD`, którego jest na DBW
              529 wierszy. Skoro portal przenosi teraz wszystkie kody, ekran ma je pokazać. */}
          {Object.keys(s.byCode)
            .sort()
            .map((code) => (
              <span key={code} className="flex items-center gap-1.5">
                <Badge variant={wariantKodu(code)} title={nazwaKodu(t, code)}>
                  {code}
                </Badge>
                <span className="tabular-nums">{nfInt.format(s.byCode[code]!.count)}</span>
              </span>
            ))}
          <span>
            <span className="text-muted-foreground">{t('tx.totalValue')}: </span>
            <span className="font-medium tabular-nums">{nfMoney.format(s.totalValue)}</span>
          </span>
        </div>
      )}

      <DataTable<TransactionRow>
        title={t('nav.transactions')}
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        state={state}
        onStateChange={onStateChange}
        isLoading={isPending}
        isFetching={isFetching}
        onRefresh={() => void refetch()}
        rowKey={(r) => r.id}
        exportFileName={`transakcje-${island?.code ?? 'wyspa'}`}
        fetchAllRows={fetchAllRows}
        reportKey="transactions"
        actions={
          /*
            ⚠️ KOREKTA CEN JAKO PRZYCISK NAD LISTĄ. Wersja z ikonką przy wierszu wymagała, żeby
            najpierw ZNALEŹĆ na liście jakiekolwiek wydanie tego towaru — a poprawia się zwykle
            coś, czego się właśnie NIE widzi (cena zerowa sprzed miesiąca). Towar i daty wybiera
            się teraz w oknie.
          */
          <Button variant="outline" size="sm" onClick={() => setKorekta(true)}>
            <Wallet className="size-4" />
            {t('korekta.przycisk')}
          </Button>
        }
        reportGroupId={groupId}
        aktywneFiltry={aktywneFiltry}
        onWyczyscFiltry={wyczyscFiltry}
        filters={
          <div className="flex flex-wrap items-end gap-3">
            {/* filtr po automacie wymaga modułu `machines` — patrz komentarz przy `canFilterByMachine` */}
            {canFilterByMachine && (
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                {t('tx.machine')}
                <Select
                  value={machineId}
                  onChange={(e) => {
                    setMachineId(e.target.value);
                    resetPage();
                  }}
                  className="h-8 w-56 text-sm"
                >
                  <option value="">{t('tx.allMachines')}</option>
                  {(machines.data?.rows ?? []).map((m) => (
                    <option key={m.id} value={m.id}>
                      VMID {m.vmid} — {m.name}
                    </option>
                  ))}
                </Select>
                {machines.error && (
                  <span className="text-xs text-destructive">{t('tx.machineFilterUnavailable')}</span>
                )}
              </label>
            )}

            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('tx.code')}
              <Select
                value={transCode}
                onChange={(e) => {
                  setTransCode(e.target.value);
                  resetPage();
                }}
                className="h-8 w-48 text-sm"
              >
                <option value="">{t('tx.allCodes')}</option>
                {kodyDoFiltra.map((c) => (
                  <option key={c} value={c}>
                    {nazwaKodu(t, c) === c ? c : `${c} — ${nazwaKodu(t, c)}`}
                  </option>
                ))}
              </Select>
            </label>

            {canFilterByEmpGroup && (
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                <span title={t('tx.groupAtIssueHint')}>{t('tx.groupAtIssue')}</span>
                <Select
                  value={employeeGroupId}
                  onChange={(e) => {
                    setEmployeeGroupId(e.target.value);
                    resetPage();
                  }}
                  className="h-8 w-56 text-sm"
                >
                  <option value="">{t('tx.allGroups')}</option>
                  {(empGroups.data?.rows ?? []).map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name} ({g.spsGroupNumber})
                    </option>
                  ))}
                </Select>
              </label>
            )}

            <label className="flex flex-col gap-1 text-xs text-muted-foreground">
              <span title={t('tx.costCenterHint')}>{t('tx.costCenter')}</span>
              <Select
                value={costCenter ?? '__wszystkie__'}
                onChange={(e) => {
                  const v = e.target.value;
                  setCostCenter(v === '__wszystkie__' ? undefined : v);
                  resetPage();
                }}
                className="h-8 w-56 text-sm"
              >
                <option value="__wszystkie__">{t('tx.allCostCenters')}</option>
                {(centra.data ?? []).map((c) => (
                  <option key={c.wartosc} value={c.wartosc}>
                    {(c.wartosc === '' ? t('tx.noCostCenter') : c.wartosc) + ' (' + nfInt.format(c.wydan) + ')'}
                  </option>
                ))}
              </Select>
            </label>

            <div className="flex flex-col gap-1 text-xs text-muted-foreground">
              {t('tx.period')}
              <div className="flex overflow-hidden rounded-md border border-border">
                {(['today', 'd7', 'd30', 'mtd', 'all'] as Preset[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => {
                      setPreset(p);
                      resetPage();
                    }}
                    aria-pressed={preset === p}
                    className={cn(
                      'px-2.5 py-1.5 text-xs transition-colors',
                      preset === p ? 'bg-primary text-primary-foreground' : 'hover:bg-accent',
                    )}
                  >
                    {t(`tx.preset.${p}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* zakres własny za cienką linią, jak w konwencji kontrolki daty */}
            <div className="flex items-end gap-2 border-l border-border pl-3">
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                {t('tx.from')}
                <Input
                  type="date"
                  value={custom.from}
                  onChange={(e) => {
                    setCustom((c) => ({ ...c, from: e.target.value }));
                    setPreset('custom');
                    resetPage();
                  }}
                  className="h-8 w-36 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                {t('tx.to')}
                <Input
                  type="date"
                  value={custom.to}
                  onChange={(e) => {
                    setCustom((c) => ({ ...c, to: e.target.value }));
                    setPreset('custom');
                    resetPage();
                  }}
                  className="h-8 w-36 text-sm"
                />
              </label>
            </div>
          </div>
        }
      />

      {/*
        ⚠️ ZAKRES DAT I AUTOMAT BIERZEMY Z FILTRÓW EKRANU, a nie pytamy o nie drugi raz. Człowiek
        właśnie na nie patrzy — drugi zestaw pól w oknie znaczyłby, że korekta może objąć inny
        okres niż ten, w którym widać rozjazd.
      */}
      {korekta && groupId !== null && (
        <KorektaCeny
          groupId={groupId}
          {...(machineId ? { machineId } : {})}
          domyslneOd={range.from ?? ''}
          domyslneDo={range.to ?? ''}
          kodyWDanych={kodyWidziane}
          onClose={() => setKorekta(false)}
        />
      )}
    </div>
  );
}
